import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import type { CreateFundingRequestPrivateDto } from '@/api/kubik/credit'
import { useAuthenticateUser } from '@/components/ui/AuthButton/hooks'
import { FormValuesFormatter } from '@/constants/financing'
import type { IApplyingFinancingOfferProps, IFinancingOfferFormValues } from '@/logic/financing/interfaces'
import { getProfile } from '@/logic/profile/reducer'
import type { RootState } from '@/redux/interfaces'
import { EFinancingProductId } from '@/types'
import { getApplyingFinancingOfferFormInitialValues } from '@/utils/getApplyingFinancingOfferFormInitialValues'
import { useRegionsMicrocreditOrganizations } from '@/utils/hooks/applyingFinancingOffer/useRegionsMicrocreditOrganizations'
import { isApplyingOfferRequestBody } from './typeguard'

type TApplyingFinancingOfferFormOptions = {
  formValues: Partial<IFinancingOfferFormValues>
  apiRequest?: (body: CreateFundingRequestPrivateDto) => void
}

const mapState = (state: RootState) => ({
  profile: getProfile()(state),
})

export const useApplyingFinancingOfferFormNew = ({ offerType, productId, requestMapper }: IApplyingFinancingOfferProps) => {
  const dispatch = useDispatch()
  const { profile } = useSelector(mapState)
  const { isAuthenticated } = useAuthenticateUser()

  const { isMFOProduct, regionsAvailableExpressMFO } = useRegionsMicrocreditOrganizations({
    productId,
  })

  const [options, setOptions] = useState<TApplyingFinancingOfferFormOptions>({ formValues: {}, apiRequest: () => {} })

  useEffect(() => {
    if (!isEmpty(options.formValues) && isAuthenticated) {
      const initialValues = getApplyingFinancingOfferFormInitialValues({
        offerType,
        productId,
        profile,
        formValues: options.formValues,
      })

      const body = FormValuesFormatter[offerType]?.(initialValues as IFinancingOfferFormValues) || initialValues

      const hasAvailableExpressMFO = !isNil(body.regionCode) && regionsAvailableExpressMFO.includes(body.regionCode)

      if (isApplyingOfferRequestBody(body)) {
        const mappedBody = requestMapper?.(body) ?? body

        if (isMFOProduct) {
          if (hasAvailableExpressMFO) {
            const formattedBody = {
              ...mappedBody,
              productId: EFinancingProductId.EXPRESS_MFO,
              productIds: [EFinancingProductId.EXPRESS_MFO],
              regionCode: mappedBody.regionCode,
              communicationMethod: '',
              city: '',
            }

            options?.apiRequest?.(formattedBody)
          }
        } else {
          // @ts-ignore
          options?.apiRequest?.(mappedBody)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerType, options, isAuthenticated])

  return {
    dispatch,
    profile,
    formValues: options.formValues,
    setOptions,
  }
}
